/**
 * Uma revisão aberta: a lista completa dos comentários que o cliente devolveu,
 * o que já foi tratado e o botão que fecha o ciclo.
 *
 * É a tela de coordenação — quem vai de fato mexer no documento trabalha pela
 * aba Partes do editor, onde o mesmo checklist aparece junto da parte. Aqui é
 * onde se conferem os itens que a leitura não conseguiu ancorar, se corrige a
 * parte de um comentário e se conclui apontando a GRD de retorno.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  FileText,
  Loader2,
  Pencil,
  Plus,
  SlashSquare,
  Trash2,
  X,
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import {
  ItemRevisao,
  RevisaoDetalhe,
  STATUS_ITEM_META,
  STATUS_REVISAO_META,
  StatusComentarioRevisao,
  TIPO_ANOTACAO_ROTULO,
} from '@/types/revisao';

const BOTAO_SECUNDARIO =
  'inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark ' +
  'text-[13px] font-semibold text-rg-ink-2 dark:text-white bg-white dark:bg-schmersal-gray-darker ' +
  'hover:bg-rg-head dark:hover:bg-schmersal-blue focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 ' +
  'disabled:opacity-50 transition-colors duration-150';

const CAMPO =
  'w-full px-3 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark ' +
  'bg-white dark:bg-schmersal-gray-darkest text-[13px] text-rg-ink-2 dark:text-white ' +
  'focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 transition-colors duration-150';

const RESOLVIDOS: StatusComentarioRevisao[] = ['feito', 'nao_aplicavel'];

export function RevisaoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { projeto } = useProjeto();
  const { t, formatarData } = useI18n();

  const [revisao, setRevisao] = useState<RevisaoDetalhe | null>(null);
  const [guias, setGuias] = useState<Array<{ id: string; numero: string; destinatario: string }>>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [modalConcluir, setModalConcluir] = useState(false);
  const [descricaoCapa, setDescricaoCapa] = useState('');
  const [novoTexto, setNovoTexto] = useState('');
  const [novoParte, setNovoParte] = useState('');
  const [adicionando, setAdicionando] = useState(false);

  const carregar = useCallback(async () => {
    if (!id) return;
    setCarregando(true);
    try {
      const res = await apiService.obterRevisao(id);
      if (res.success && res.data) {
        setRevisao(res.data);
        setDescricaoCapa(res.data.descricaoCapa ?? '');
        setErro(null);
      } else {
        setErro(res.message ?? t('revisoes.revisoes.detalhe.naoEncontrada'));
      }
    } catch {
      setErro(t('revisoes.revisoes.detalhe.erroCarregar'));
    } finally {
      setCarregando(false);
    }
  }, [id, t]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!projeto) return;
    apiService.listarGuiasRemessa(projeto.id).then((res) => {
      if (res.success && res.data) setGuias(res.data.map((g) => ({ id: g.id, numero: g.numero, destinatario: g.destinatario })));
    });
  }, [projeto]);

  const pendentes = useMemo(
    () => (revisao ? revisao.itens.filter((i) => !RESOLVIDOS.includes(i.status)).length : 0),
    [revisao],
  );

  const encerrada = revisao?.status === 'concluida' || revisao?.status === 'cancelada';

  const atualizarItem = async (
    item: ItemRevisao,
    mudanca: { status?: StatusComentarioRevisao; parte?: string | null; resolucao?: string },
  ) => {
    setSalvando(item.id);
    try {
      const res = await apiService.atualizarComentarioRevisao(item.id, mudanca);
      if (!res.success) {
        setErro(res.message ?? t('revisoes.revisoes.detalhe.erroSalvar'));
        return;
      }
      setErro(null);
      setRevisao((prev) =>
        prev
          ? {
              ...prev,
              status: prev.status === 'aberta' && mudanca.status && mudanca.status !== 'pendente' ? 'em_andamento' : prev.status,
              itens: prev.itens.map((i) =>
                i.id === item.id
                  ? {
                      ...i,
                      status: mudanca.status ?? i.status,
                      parte: mudanca.parte !== undefined ? mudanca.parte : i.parte,
                      parteAuto: mudanca.parte !== undefined ? false : i.parteAuto,
                      resolucao: mudanca.resolucao !== undefined ? mudanca.resolucao : i.resolucao,
                    }
                  : i,
              ),
            }
          : prev,
      );
    } catch {
      setErro(t('revisoes.revisoes.detalhe.erroSalvarAlteracao'));
    } finally {
      setSalvando(null);
    }
  };

  const adicionarItem = async () => {
    if (!revisao || !novoTexto.trim()) return;
    setAdicionando(true);
    try {
      const res = await apiService.adicionarComentarioRevisao(revisao.id, {
        texto: novoTexto,
        parte: novoParte || null,
      });
      if (res.success) {
        setNovoTexto('');
        setNovoParte('');
        await carregar();
      } else {
        setErro(res.message ?? t('revisoes.revisoes.detalhe.erroAdicionar'));
      }
    } finally {
      setAdicionando(false);
    }
  };

  const removerItem = async (item: ItemRevisao) => {
    if (!confirm(t('revisoes.revisoes.detalhe.confirmarRemover'))) return;
    const res = await apiService.removerComentarioRevisao(item.id);
    if (res.success) await carregar();
    else setErro(res.message ?? t('revisoes.revisoes.detalhe.erroRemover'));
  };

  const cancelar = async () => {
    if (!revisao) return;
    const motivo = prompt(t('revisoes.revisoes.detalhe.promptCancelar'));
    if (motivo === null) return;
    const res = await apiService.cancelarRevisao(revisao.id, motivo);
    if (res.success) await carregar();
    else setErro(res.message ?? t('revisoes.revisoes.detalhe.erroCancelar'));
  };

  if (carregando) {
    return (
      <div className="flex items-center gap-2 px-5 py-8 text-[13px] text-rg-ink-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('comum.estado.carregando')}
      </div>
    );
  }

  if (!revisao) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/revisoes')} className={BOTAO_SECUNDARIO}>
          <ArrowLeft className="h-4 w-4" />
          {t('comum.acao.voltar')}
        </button>
        <p className="text-[13.5px] text-rg-ink-3 dark:text-schmersal-gray-light">
          {erro ?? t('revisoes.revisoes.detalhe.naoEncontrada')}
        </p>
      </div>
    );
  }

  const numeroEmitenteNaCapa = revisao.apreciacao.numeroEmitente || revisao.apreciacao.numero;
  const tratados = revisao.itens.length - pendentes;
  const percentual = revisao.itens.length > 0 ? Math.round((tratados / revisao.itens.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="min-w-0">
          <button
            onClick={() => navigate('/revisoes')}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-rg-ink-3 dark:text-rg-ink-4 hover:text-schmersal-blue mb-1"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('revisoes.revisoes.lista.titulo')}
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-rg-ink dark:text-white truncate">
            {t('revisoes.revisoes.detalhe.titulo', {
              numero: revisao.apreciacao.numero,
              revisao: revisao.numeroRevisao ?? revisao.rodada,
            })}
          </h1>
          <p className="mt-0.5 text-[13.5px] text-rg-ink-3 dark:text-schmersal-gray-light">
            {revisao.apreciacao.equipamento ?? revisao.apreciacao.titulo}
            {revisao.guiaOrigem &&
              ` · ${t('revisoes.revisoes.detalhe.devolvidoNa', { guia: revisao.guiaOrigem.numero })}`}
            {` · ${t('revisoes.revisoes.detalhe.recebidoEm', { data: formatarData(revisao.recebidaEm) })}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <span className={`inline-flex px-2.5 py-1 rounded-md text-[12px] font-semibold ${STATUS_REVISAO_META[revisao.status].tom}`}>
            {t(`revisoes.revisoes.status.${revisao.status}`)}
          </span>
          {revisao.arquivoUrl && (
            <a href={revisao.arquivoUrl} target="_blank" rel="noreferrer" className={BOTAO_SECUNDARIO}>
              <FileText className="h-4 w-4" />
              {t('revisoes.revisoes.detalhe.pdfComentado')}
            </a>
          )}
          <Link to={`/apreciacoes/${revisao.apreciacao.id}/editar`} className={BOTAO_SECUNDARIO}>
            <Pencil className="h-4 w-4" />
            {t('revisoes.revisoes.detalhe.editarApreciacao')}
          </Link>
          {!encerrada && (
            <>
              <button onClick={cancelar} className={BOTAO_SECUNDARIO}>
                {t('revisoes.revisoes.detalhe.cancelarRevisao')}
              </button>
              <button
                onClick={() => setModalConcluir(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-schmersal-blue
                           dark:bg-schmersal-blue-light text-white text-[13px] font-semibold shadow-sm
                           hover:bg-schmersal-blue-dark transition-colors duration-150"
              >
                <Check className="h-4 w-4" />
                {t('revisoes.revisoes.detalhe.concluirRevisao')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="text-[13px] font-semibold text-rg-ink-1 dark:text-white">
            {t('revisoes.revisoes.detalhe.progresso', { tratados, total: revisao.itens.length })}
          </span>
          <div className="h-1.5 flex-1 min-w-[160px] rounded-full bg-rg-line dark:bg-schmersal-gray-dark overflow-hidden">
            <div
              className={`h-full rounded-full transition-[width] duration-300 ${percentual === 100 ? 'bg-green-600' : 'bg-schmersal-blue'}`}
              style={{ width: `${percentual}%` }}
            />
          </div>
          <span className="text-[12.5px] text-rg-ink-3 dark:text-rg-ink-4">
            {t('revisoes.revisoes.detalhe.resumoContagem', {
              feitos: revisao.comentarios.feitos,
              naoAplicaveis: revisao.comentarios.naoAplicaveis,
              pendentes,
            })}
          </span>
        </div>
        {revisao.observacoes && (
          <p className="mt-2 text-[12.5px] text-rg-ink-3 dark:text-rg-ink-4 whitespace-pre-line">{revisao.observacoes}</p>
        )}

        {/* O que esta revisão imprime na capa. O número e o sufixo do Nº
            Emitente saem do encadeamento e não se editam aqui; o texto da
            coluna DESCRIÇÃO, sim. */}
        <div className="mt-3 pt-3 border-t border-rg-line dark:border-schmersal-gray-dark flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-[12px] font-semibold text-rg-ink-3 dark:text-schmersal-gray-light">
            {t('revisoes.revisoes.detalhe.capa.rotulo')}
          </span>
          <span className="text-[12.5px] text-rg-ink-2 dark:text-white">
            {t('revisoes.revisoes.detalhe.capa.linha')}{' '}
            <b className="font-semibold tabular-nums">
              {t('revisoes.revisoes.detalhe.capa.numero', { numero: revisao.numeroRevisao ?? '—' })}
            </b>
            {' · '}{t('revisoes.revisoes.detalhe.capa.numeroEmitente')}{' '}
            <b className="font-semibold">{numeroEmitenteNaCapa}</b>
            {' · '}{t('revisoes.revisoes.detalhe.capa.colunaArcelor')}
          </span>
          <label className="flex items-center gap-2 ml-auto">
            <span className="text-[12px] text-rg-ink-3 dark:text-schmersal-gray-light">
              {t('revisoes.revisoes.detalhe.capa.descricao')}
            </span>
            <input
              value={descricaoCapa}
              disabled={revisao.status === 'cancelada'}
              onChange={(e) => setDescricaoCapa(e.target.value)}
              onBlur={async () => {
                if (descricaoCapa === (revisao.descricaoCapa ?? '')) return;
                const res = await apiService.atualizarRevisao(revisao.id, { descricaoCapa });
                if (res.success && res.data) {
                  setDescricaoCapa(res.data.descricaoCapa ?? '');
                  setRevisao((prev) => (prev ? { ...prev, descricaoCapa: res.data!.descricaoCapa } : prev));
                } else {
                  setErro(res.message ?? t('revisoes.revisoes.detalhe.erroSalvarDescricao'));
                }
              }}
              maxLength={60}
              className={`${CAMPO} w-64 text-[12.5px]`}
            />
          </label>
        </div>
        {revisao.status === 'concluida' && (
          <p className="mt-2 text-[12.5px] text-green-800 dark:text-green-300">
            {t('revisoes.revisoes.detalhe.concluidaEm', {
              data: revisao.concluidaEm ? formatarData(revisao.concluidaEm) : '—',
            })}
            {revisao.guiaRetorno &&
              ` · ${t('revisoes.revisoes.detalhe.retramitadaNa', { guia: revisao.guiaRetorno.numero })}`}
            {` · ${t('revisoes.revisoes.detalhe.apreciacaoTramitada')}`}
          </p>
        )}
      </div>

      {erro && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-300" />
          <span className="text-[13px] text-red-800 dark:text-red-200">{erro}</span>
        </div>
      )}

      <div className="space-y-2.5">
        {revisao.itens.map((item) => (
          <div
            key={item.id}
            className={`rounded-xl border bg-white dark:bg-schmersal-gray-darker p-4 ${
              RESOLVIDOS.includes(item.status)
                ? 'border-rg-line dark:border-schmersal-gray-dark opacity-80'
                : 'border-amber-300/70 dark:border-amber-500/40'
            }`}
          >
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-[11.5px] font-semibold text-rg-ink-4">
                {t('revisoes.revisoes.detalhe.item.cabecalho', {
                  indice: item.indice,
                  pagina: item.pagina || '—',
                  tipo: TIPO_ANOTACAO_ROTULO[item.tipo]
                    ? t(`revisoes.revisoes.tipoAnotacao.${item.tipo}`)
                    : item.tipo,
                })}
              </span>
              {item.autor && <span className="text-[11.5px] text-rg-ink-4">· {item.autor}</span>}
              {item.dataComentario && (
                <span className="text-[11.5px] text-rg-ink-4">
                  · {formatarData(item.dataComentario)}
                </span>
              )}
              <span className={`ml-auto inline-flex px-2 py-0.5 rounded-md text-[11.5px] font-semibold ${STATUS_ITEM_META[item.status].tom}`}>
                {t(`revisoes.revisoes.statusItem.${item.status}`)}
              </span>
            </div>

            {item.trechoDestacado && (
              <p className="mb-2 text-[12.5px] italic text-rg-ink-3 dark:text-rg-ink-4 border-l-2 border-amber-400 pl-2.5">
                “{item.trechoDestacado}”
              </p>
            )}

            <p className="text-[13.5px] text-rg-ink-1 dark:text-white whitespace-pre-line">{item.texto}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <select
                aria-label={t('revisoes.revisoes.detalhe.item.parteAria')}
                value={item.parte ?? ''}
                disabled={encerrada}
                onChange={(e) => atualizarItem(item, { parte: e.target.value || null })}
                className={`${CAMPO} w-auto max-w-[280px] ${item.parteAuto ? 'italic' : ''}`}
                title={item.parteAuto ? t('revisoes.revisoes.detalhe.item.parteAutoTitulo') : undefined}
              >
                <option value="">{t('revisoes.revisoes.detalhe.item.semParteDefinida')}</option>
                {revisao.partesDisponiveis.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
                {/* A parte lida do PDF pode não bater com nenhuma cadastrada. */}
                {item.parte && !revisao.partesDisponiveis.includes(item.parte) && (
                  <option value={item.parte}>
                    {t('revisoes.revisoes.detalhe.item.parteDoPdf', { parte: item.parte })}
                  </option>
                )}
              </select>

              {!encerrada && (
                <>
                  <button
                    type="button"
                    onClick={() => atualizarItem(item, { status: item.status === 'feito' ? 'pendente' : 'feito' })}
                    disabled={salvando === item.id}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12.5px] font-semibold transition-colors duration-150 ${
                      item.status === 'feito'
                        ? 'bg-green-600 text-white'
                        : 'border border-rg-line dark:border-schmersal-gray-dark text-rg-ink-3 dark:text-rg-ink-4 hover:bg-rg-head dark:hover:bg-schmersal-gray-darkest'
                    }`}
                  >
                    <Check className="h-3.5 w-3.5" />
                    {t('revisoes.revisoes.detalhe.item.acaoFeito')}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      atualizarItem(item, { status: item.status === 'nao_aplicavel' ? 'pendente' : 'nao_aplicavel' })
                    }
                    disabled={salvando === item.id}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12.5px] font-semibold transition-colors duration-150 ${
                      item.status === 'nao_aplicavel'
                        ? 'bg-slate-600 text-white'
                        : 'border border-rg-line dark:border-schmersal-gray-dark text-rg-ink-3 dark:text-rg-ink-4 hover:bg-rg-head dark:hover:bg-schmersal-gray-darkest'
                    }`}
                  >
                    <SlashSquare className="h-3.5 w-3.5" />
                    {t('revisoes.revisoes.detalhe.item.acaoNaoSeAplica')}
                  </button>
                  {item.tipo === 'Manual' && (
                    <button
                      type="button"
                      onClick={() => removerItem(item)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12.5px] font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t('comum.acao.remover')}
                    </button>
                  )}
                </>
              )}
            </div>

            <textarea
              value={item.resolucao ?? ''}
              disabled={encerrada}
              placeholder={t('revisoes.revisoes.detalhe.item.resolucaoPlaceholder')}
              onChange={(e) =>
                setRevisao((prev) =>
                  prev ? { ...prev, itens: prev.itens.map((i) => (i.id === item.id ? { ...i, resolucao: e.target.value } : i)) } : prev,
                )
              }
              onBlur={(e) => atualizarItem(item, { resolucao: e.target.value })}
              rows={1}
              className={`${CAMPO} mt-2 text-[12.5px]`}
            />
          </div>
        ))}
      </div>

      {!encerrada && (
        <div className="rounded-xl border border-dashed border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker p-4">
          <p className="text-[13px] font-semibold text-rg-ink-1 dark:text-white mb-2">
            {t('revisoes.revisoes.detalhe.adicionar.titulo')}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={novoTexto}
              onChange={(e) => setNovoTexto(e.target.value)}
              placeholder={t('revisoes.revisoes.detalhe.adicionar.placeholder')}
              className={CAMPO}
            />
            <select value={novoParte} onChange={(e) => setNovoParte(e.target.value)} className={`${CAMPO} sm:w-64`}>
              <option value="">{t('revisoes.revisoes.detalhe.adicionar.semParte')}</option>
              {revisao.partesDisponiveis.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button onClick={adicionarItem} disabled={adicionando || !novoTexto.trim()} className={BOTAO_SECUNDARIO}>
              {adicionando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('comum.acao.adicionar')}
            </button>
          </div>
        </div>
      )}

      {modalConcluir && (
        <ModalConcluir
          pendentes={pendentes}
          guias={guias}
          onFechar={() => setModalConcluir(false)}
          onConcluida={async () => {
            setModalConcluir(false);
            await carregar();
          }}
          concluir={(dados) => apiService.concluirRevisao(revisao.id, dados)}
        />
      )}
    </div>
  );
}

function ModalConcluir({
  pendentes,
  guias,
  onFechar,
  onConcluida,
  concluir,
}: {
  pendentes: number;
  guias: Array<{ id: string; numero: string; destinatario: string }>;
  onFechar: () => void;
  onConcluida: () => void;
  concluir: (dados: { guiaRetornoId?: string | null; observacoes?: string | null; forcar?: boolean }) => Promise<{ success: boolean; message?: string }>;
}) {
  const { t } = useI18n();
  const [guiaRetornoId, setGuiaRetornoId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [forcar, setForcar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-schmersal-gray-darker shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-rg-line dark:border-schmersal-gray-dark">
          <h2 className="text-[15px] font-bold text-rg-ink dark:text-white">{t('revisoes.revisoes.modalConcluir.titulo')}</h2>
          <button onClick={onFechar} className="p-1 rounded-md text-rg-ink-4 hover:bg-rg-head dark:hover:bg-schmersal-gray-darkest">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {pendentes > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
              <AlertCircle className="h-4 w-4 mt-0.5 text-amber-700 dark:text-amber-300 shrink-0" />
              <div className="text-[12.5px] text-amber-900 dark:text-amber-100">
                <p>
                  {t(
                    pendentes === 1
                      ? 'revisoes.revisoes.modalConcluir.pendentesUm'
                      : 'revisoes.revisoes.modalConcluir.pendentesMuitos',
                    { pendentes },
                  )}
                </p>
                <label className="mt-1.5 flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={forcar} onChange={(e) => setForcar(e.target.checked)} className="rounded" />
                  {t('revisoes.revisoes.modalConcluir.forcar')}
                </label>
              </div>
            </div>
          )}

          <div>
            <label className="block text-[12px] font-semibold text-rg-ink-2 dark:text-white mb-1">
              {t('revisoes.revisoes.modalConcluir.grdRotulo')}
            </label>
            <select value={guiaRetornoId} onChange={(e) => setGuiaRetornoId(e.target.value)} className={CAMPO}>
              <option value="">{t('revisoes.revisoes.modalConcluir.grdVazia')}</option>
              {guias.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.numero} — {g.destinatario}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-rg-ink-2 dark:text-white mb-1">{t('comum.rotulo.observacoes')}</label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} className={CAMPO} />
          </div>

          {erro && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-900/20 px-3 py-2">
              <AlertCircle className="h-4 w-4 mt-0.5 text-red-600 dark:text-red-300 shrink-0" />
              <span className="text-[12.5px] text-red-800 dark:text-red-200">{erro}</span>
            </div>
          )}

          <p
            className="text-[12px] text-rg-ink-4"
            dangerouslySetInnerHTML={{ __html: t('revisoes.revisoes.modalConcluir.aviso') }}
          />
        </div>

        <div className="flex justify-end gap-2.5 px-5 py-4 border-t border-rg-line dark:border-schmersal-gray-dark">
          <button onClick={onFechar} className={BOTAO_SECUNDARIO}>
            {t('comum.acao.cancelar')}
          </button>
          <button
            onClick={async () => {
              setSalvando(true);
              setErro(null);
              try {
                const res = await concluir({
                  guiaRetornoId: guiaRetornoId || null,
                  observacoes: observacoes || null,
                  forcar,
                });
                if (res.success) onConcluida();
                else setErro(res.message ?? t('revisoes.revisoes.modalConcluir.erroConcluir'));
              } finally {
                setSalvando(false);
              }
            }}
            disabled={salvando || (pendentes > 0 && !forcar)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-schmersal-blue
                       dark:bg-schmersal-blue-light text-white text-[13px] font-semibold shadow-sm
                       hover:bg-schmersal-blue-dark disabled:opacity-50 transition-colors duration-150"
          >
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('revisoes.revisoes.modalConcluir.acaoConcluir')}
          </button>
        </div>
      </div>
    </div>
  );
}
